/**
 * Menu 2 · Submenu C: Preview-Zandbak — afgeschermde testomgeving. Toont de eigen
 * training exact als Academy-tegel (16:9, 115% hover-portal) en een detail-pop-up,
 * ongeacht publicatiestatus, met (her)publiceren vanuit de zandbak.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, X, Star, CalendarDays, MapPin, Clock, Users } from 'lucide-react';
import { getProviderTrainings, togglePublishTraining } from '../services/opleidingenApi';
import { getPreview, PreviewData } from '../services/opleiderCockpitApi';
import { AcademyTegel } from '../components/academy/AcademyTegel';
import type { AcademyTraining } from '../services/medewerkerApi';
import { ACCENT_KLEUR, LEERVORM, accentVoor, datumLang, prijs } from '../components/academy/academyStijl';

const Blok: React.FC<{ titel: string; tekst?: string | null }> = ({ titel, tekst }) =>
  tekst?.trim() ? (
    <section>
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">{titel}</h3>
      <p className="mt-1.5 whitespace-pre-line text-[15px] leading-relaxed text-slate-200">{tekst.trim()}</p>
    </section>
  ) : null;

const Balk: React.FC<{ label: string; waarde: number }> = ({ label, waarde }) => (
  <div>
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-slate-300">{label}</span>
      <span className="inline-flex items-center gap-1 font-semibold text-white"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />{waarde.toFixed(1)}</span>
    </div>
    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-amber-400" style={{ width: `${(waarde / 5) * 100}%` }} /></div>
  </div>
);

const DetailPreview: React.FC<{ data: PreviewData; onClose: () => void }> = ({ data, onClose }) => {
  const { t } = useTranslation('pages');
  const tr: AcademyTraining = data.tegel;
  const d = data.detail;
  const accent = ACCENT_KLEUR[accentVoor(tr)];
  const vorm = LEERVORM[tr.leervorm];
  const VormIcon = vorm.icon;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#0f1420] text-white shadow-2xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="relative aspect-video overflow-hidden rounded-t-2xl bg-[#10151f]">
          {tr.omslagfoto_url ? <img src={tr.omslagfoto_url} alt="" className="absolute inset-0 h-full w-full object-cover" /> : (
            <div className="absolute inset-0 flex items-center justify-center" style={{ background: `radial-gradient(120% 90% at 30% 20%, ${accent}55 0%, #0b1020 60%, #05070c 100%)` }}>
              <span className="text-7xl">{tr.subcategorie_emoji || tr.categorie_emoji}</span>
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#0f1420] to-transparent" />
          <button type="button" onClick={onClose} className="absolute right-3 top-3 rounded-full bg-black/60 p-2 hover:bg-black/80" aria-label={t('opleiderCockpit.sluiten')}><X className="h-5 w-5" /></button>
          <div className="absolute bottom-4 left-5 right-5">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white ${vorm.badge}`}><VormIcon className="h-3.5 w-3.5" />{vorm.label}</span>
            <h2 className="mt-2 text-2xl font-bold">{tr.titel}</h2>
            <p className="text-sm text-slate-300">{tr.organisatie_naam}{tr.subcategorie_naam ? ` · ${tr.subcategorie_naam}` : ''}</p>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex flex-wrap gap-4 text-sm text-slate-300">
            <span className="inline-flex items-center gap-1.5 font-semibold text-white">{prijs(tr.netto_bedrag)} <span className="text-slate-400 line-through font-normal">{prijs(tr.bruto_bedrag)}</span></span>
            {tr.duur && <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" />{tr.duur}</span>}
            {tr.locaties && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{tr.locaties}</span>}
          </div>
          {tr.pitch && <p className="text-[15px] italic text-slate-200">“{tr.pitch}”</p>}
          <Blok titel={t('opleiderCockpit.preview.samenvatting')} tekst={d.samenvatting} />
          <Blok titel={t('opleiderCockpit.preview.leerdoelen')} tekst={d.leerdoelen} />
          <Blok titel={t('opleiderCockpit.preview.programma')} tekst={d.programma_onderdelen} />
          <Blok titel={t('opleiderCockpit.preview.doelgroep')} tekst={d.doelgroep} />
          {data.sessies.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1.5"><CalendarDays className="h-4 w-4" />{t('opleiderCockpit.preview.sessies')}</h3>
              <ul className="mt-2 space-y-1.5">
                {data.sessies.map((s) => (
                  <li key={s.id} className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2 text-sm">
                    <span>{s.naam || t('opleiderCockpit.preview.sessie')}{s.lessen?.[0]?.start_at ? ` · ${datumLang(s.lessen[0].start_at)}` : ''}</span>
                    <span className="inline-flex items-center gap-1 text-slate-300"><Users className="h-3.5 w-3.5" />{s.geboekt}/{s.max_deelnemers}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">{t('opleiderCockpit.preview.reviews')}</h3>
            {data.reviews.balken ? (
              <div className="mt-2 space-y-3">
                <Balk label={t('opleiderCockpit.reviews.inhoud')} waarde={data.reviews.balken.relevantie} />
                <Balk label={t('opleiderCockpit.reviews.toepasbaarheid')} waarde={data.reviews.balken.toepasbaarheid} />
                <Balk label={t('opleiderCockpit.reviews.lesmateriaal')} waarde={data.reviews.balken.expertise} />
              </div>
            ) : (
              <p className="mt-1.5 text-sm text-slate-400">{t('opleiderCockpit.preview.reviewsDrempel', { n: data.reviews.aantal })}</p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export const OpleiderPreviewZandbakPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const [gekozen, setGekozen] = useState<number | null>(null);
  const [open, setOpen] = useState(false);

  const trainingen = useQuery({ queryKey: ['provider-trainings'], queryFn: getProviderTrainings });
  const id = gekozen ?? trainingen.data?.[0]?.id ?? null;
  const preview = useQuery({ queryKey: ['opleider-preview', id], queryFn: () => getPreview(id!), enabled: id !== null });
  const publiceer = useMutation({
    mutationFn: togglePublishTraining,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-trainings'] });
      queryClient.invalidateQueries({ queryKey: ['opleider-preview', id] });
    },
  });
  const huidige = trainingen.data?.find((x) => x.id === id);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-sm text-gray-500">{t('opleiderCockpit.preview.intro')}</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={id ?? ''} onChange={(e) => setGekozen(parseInt(e.target.value, 10))} className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
            {(trainingen.data ?? []).map((tr) => <option key={tr.id} value={tr.id}>{tr.titel}{tr.is_published ? '' : ` (${t('opleider.concept')})`}</option>)}
          </select>
          {huidige && (
            <button type="button" disabled={publiceer.isPending}
              onClick={() => { if (window.confirm(huidige.is_published ? t('opleiderCockpit.preview.bevestigDepubliceer') : t('opleiderCockpit.preview.bevestigPubliceer'))) publiceer.mutate(huidige.id); }}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${huidige.is_published ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50' : 'bg-[#0B1F3A] text-white hover:bg-[#1A3459]'} disabled:opacity-60`}>
              {huidige.is_published ? <><EyeOff className="w-4 h-4" />{t('opleider.unpublish')}</> : <><Eye className="w-4 h-4" />{t('opleiderCockpit.preview.publiceer')}</>}
            </button>
          )}
        </div>
      </div>

      {trainingen.data && trainingen.data.length === 0 ? (
        <p className="p-10 text-center text-gray-500 bg-white rounded-xl border border-gray-200">{t('opleider.noTrainingsYet')}</p>
      ) : (
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 sm:p-10">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-4">{t('opleiderCockpit.preview.zandbakLabel')}</p>
          {preview.isLoading || !preview.data ? (
            <div className="h-56 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B1F3A]" /></div>
          ) : (
            <div className="flex flex-wrap items-start gap-10">
              <div className="w-[82vw] max-w-[21rem] sm:w-[20rem] lg:w-[21rem]">
                <AcademyTegel training={preview.data.tegel} onOpen={() => setOpen(true)} className="w-full" />
              </div>
              <div className="flex-1 min-w-[16rem] text-gray-600 text-sm space-y-2">
                <p className="text-gray-900 font-semibold">{t('opleiderCockpit.preview.checklist')}</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>{t('opleiderCockpit.preview.check169')}</li>
                  <li>{t('opleiderCockpit.preview.checkHover')}</li>
                  <li>{t('opleiderCockpit.preview.checkDetail')}</li>
                  {preview.data.tegel.academy_categorie && (
                    <li>{t('opleiderCockpit.preview.checkFiscaal', { label: preview.data.tegel.wkr_label ?? '—', categorie: preview.data.tegel.categorie_label })}</li>
                  )}
                </ul>
                <button type="button" onClick={() => setOpen(true)} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#0B1F3A] px-4 py-2 text-white hover:bg-[#1A3459]"><Eye className="w-4 h-4" />{t('opleiderCockpit.preview.openDetail')}</button>
              </div>
            </div>
          )}
        </div>
      )}
      {open && preview.data && <DetailPreview data={preview.data} onClose={() => setOpen(false)} />}
    </div>
  );
};

export default OpleiderPreviewZandbakPage;
